import Sentry from "@similarweb/sentry-logger";

import type { BenchmarkTabName } from "app/pages/benchmark-tracker/config";
import type { BenchmarkMetricName, MetricEntity } from "app/pages/benchmark-tracker/types/api.types";
import type { IFetchService } from "app/services/fetchService";
import { ResponseError } from "app/services/fetchService";

import type { BenchmarkGranularity } from "./constants";
import { BENCHMARKS_ENDPOINT } from "./constants";

type MetricApiServiceEntity = {
    fetchMetric<R = MetricEntity>(
        metric: BenchmarkMetricName,
        tabName: BenchmarkTabName,
        benchmarkId: string,
        country: number,
        startDate: string,
        granularity: BenchmarkGranularity,
        isAllAssetsData?: boolean,
    ): Promise<R>;
};

export const metricApiService = (fetchService: IFetchService): MetricApiServiceEntity => {
    return {
        fetchMetric: async <R = MetricEntity>(
            metric: BenchmarkMetricName,
            tabName: BenchmarkTabName,
            benchmarkId: string,
            country: number,
            startDate: string,
            granularity: BenchmarkGranularity,
            isAllAssetsData?: boolean,
        ): Promise<R> => {
            try {
                const metricData = await fetchService.get(`${BENCHMARKS_ENDPOINT}/${tabName}/${metric}`, {
                    id: benchmarkId,
                    country,
                    startDate,
                    granularity,
                    isAllAssetsData,
                });
                return metricData as R;
            } catch (error) {
                const status = error instanceof ResponseError ? String(error.status) : "unknown";

                Sentry.errorTracker({
                    message: "BenchmarkTracker API service: Error when fetching metric",
                    e: error,
                    level: "error",
                    includeCustomFingerPrint: ["benchmark-tracker-api-fetch-metric", status],
                    params: {
                        extra: {
                            benchmarkId,
                            country,
                            granularity,
                            isAllAssetsData,
                            metric,
                            startDate,
                            tabName,
                        },
                    },
                });

                throw error;
            }
        },
    };
};
